import type { Database } from '../../database.types';
import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BuyerRequestRow = Database['public']['Tables']['buyer_requests']['Row'];
export type BuyerRequestStatus = Database['public']['Enums']['request_status'];
export type BuyerRequestType = Database['public']['Enums']['request_type'];

type DealJoin = Pick<
  Database['public']['Tables']['deals']['Row'],
  | 'id'
  | 'title'
  | 'slug'
  | 'zone'
  | 'reference_id'
  | 'asking_price'
  | 'discount_pct'
  | 'dld_confidence'
  | 'dld_scope'
>;

export type BuyerRequestRowJoined = BuyerRequestRow & {
  deals: DealJoin | null;
};

export type BuyerRequestsTabFilter = 'all' | BuyerRequestStatus;

// ---------------------------------------------------------------------------
// DB → UI status mapping (ADR-025 §3 — kept here to avoid drift)
// ---------------------------------------------------------------------------
// The DB enum `request_status` values are lower_snake_case (`treated` etc.),
// but the historic mocks + dashboard pills use UPPER labels and `PROCESSED`
// instead of `treated`. This map is the single source of truth for the
// conversion in read-only direction.

export const STATUS_DB_TO_UI: Record<
  BuyerRequestStatus,
  'NEW' | 'IN_PROGRESS' | 'PROCESSED' | 'CLOSED'
> = {
  new: 'NEW',
  in_progress: 'IN_PROGRESS',
  treated: 'PROCESSED',
  closed: 'CLOSED',
};

// ---------------------------------------------------------------------------
// Select shape
// ---------------------------------------------------------------------------

const SELECT_WITH_DEAL =
  '*, deals:deal_id (id, title, slug, zone, reference_id, asking_price, discount_pct, dld_confidence, dld_scope)';

// ---------------------------------------------------------------------------
// listBuyerRequests — SELECT own requests, optional tab filter
// ---------------------------------------------------------------------------
// RLS: `buyer_requests_buyer_select` (20260506150000 lines 426-436) filters
// `user_id = auth.uid()` + `is_email_verified()=true`. Admins also see all
// rows (used by AdminRequestsPage). For the buyer dashboard the result is
// naturally scoped to the current buyer — we do not need to add an `eq()`.

export async function listBuyerRequests(
  tab: BuyerRequestsTabFilter = 'all',
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<BuyerRequestRowJoined[]> {
  let q = client.from('buyer_requests').select(SELECT_WITH_DEAL);

  if (tab !== 'all') {
    q = q.eq('status', tab);
  }

  const { data, error } = await q.order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as unknown as BuyerRequestRowJoined[];
}

// ---------------------------------------------------------------------------
// Dashboard KPI counts — head:true, never materialise the row list (Spec §M7)
// ---------------------------------------------------------------------------
// RLS (`buyer_requests_buyer_select`) already scopes to the current buyer, so
// the count is naturally per-user. `head:true` returns only the exact count,
// not the rows — no pagination, no payload.

const ACTIVE_STATUSES: BuyerRequestStatus[] = ['new', 'in_progress'];
const PROCESSED_STATUSES: BuyerRequestStatus[] = ['treated', 'closed'];

async function countBuyerRequestsByStatus(
  statuses: BuyerRequestStatus[],
  client: DdmSupabaseClient,
): Promise<number> {
  const { count, error } = await client
    .from('buyer_requests')
    .select('id', { count: 'exact', head: true })
    .in('status', statuses);
  if (error) throw error;
  return count ?? 0;
}

export async function countActiveBuyerRequests(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<number> {
  return countBuyerRequestsByStatus(ACTIVE_STATUSES, client);
}

export async function countProcessedBuyerRequests(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<number> {
  return countBuyerRequestsByStatus(PROCESSED_STATUSES, client);
}

// ---------------------------------------------------------------------------
// createBuyerRequest — INSERT a new interest or callback
// ---------------------------------------------------------------------------
// DB triggers run automatically on INSERT:
//   - buyer_requests_assign_reference_id_trg → reference_id REQ-YYYY-NNNNNN
//   - buyer_requests_increment_nb_demandes_trg → deals.nb_demandes ++
//   - audit_log via separate trigger (Phase C)
//
// Constraints mirrored in the Zod schema upstream (caller validates first):
//   - message: nullable, max 500 chars (CHECK buyer_requests_message_length)
//   - callback_slot: required when type='callback', enum (CHECK *_callback_slot_required + *_values)
//   - callback_date_preference: CURRENT_DATE .. CURRENT_DATE + 14 days
//
// Errors:
//   - PostgrestError 23514 → CHECK failed (Zod missed something) — propagated.
//   - PostgrestError 42501 → RLS denied (user not email-verified) — propagated.

export type CreateBuyerRequestInput = {
  userId: string;
  dealId: string;
  type: BuyerRequestType;
  // E.164 phone the buyer entered in the modal (ADR-050). Persisted on the
  // request so it reaches the back office + Bitrix24 even when it differs from
  // (or is absent on) the profile.
  contactPhone?: string | null;
  message?: string | null;
  callbackSlot?: '09-12' | '12-15' | '15-18' | '18-21' | null;
  callbackDatePreference?: string | null; // ISO date 'YYYY-MM-DD'
};

type BuyerRequestInsert = Database['public']['Tables']['buyer_requests']['Insert'];

export async function createBuyerRequest(
  input: CreateBuyerRequestInput,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<BuyerRequestRowJoined> {
  // `reference_id` is NOT NULL in the schema but is filled by
  // `buyer_requests_assign_reference_id_trg` BEFORE INSERT — we send an
  // empty placeholder that the trigger overwrites.
  const insertPayload: BuyerRequestInsert = {
    reference_id: '',
    user_id: input.userId,
    deal_id: input.dealId,
    type: input.type,
    contact_phone: input.contactPhone ?? null,
    message: input.message ?? null,
    callback_slot: input.callbackSlot ?? null,
    callback_date_preference: input.callbackDatePreference ?? null,
  };

  const { data, error } = await client
    .from('buyer_requests')
    .insert(insertPayload)
    .select(SELECT_WITH_DEAL)
    .single();

  if (error) throw error;
  return data as unknown as BuyerRequestRowJoined;
}
